<template>
  <v-container fluid class="pa-16 pt-8">
    <v-form ref="form" v-model="valid">
      <v-row>
        <!-- left Section: Profile Upload -->
        <v-col cols="12" md="3" class="d-flex justify-center">
          <ProfileUpload
            :photo="form.photo"
            :declaration="false"
            :isReadOnly="true"
          />
        </v-col>
        <v-col cols="12" md="1" class="d-flex justify-center pl-0 ml-0">
          <v-btn icon :disabled="isReadOnly">
            <v-icon
              class="cursor-pointer"
              color="green"
              @click="$refs.photo.click()"
            >
              mdi-upload
            </v-icon>
          </v-btn>

          <!-- Hidden File Input for Replace -->
          <input
            type="file"
            ref="photo"
            style="display: none"
            accept="application/pdf, image/png, image/jpeg, image/jpg"
            @change="onFileReplaced($event, 'photo', 'photoImage')"
          />
        </v-col>
        <!-- right Section: Form Fields -->
        <v-col cols="12" md="8">
          <v-row>
            <v-col cols="12" sm="4">
              <label class="form-label">DEB ID</label>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    v-bind="attrs"
                    v-on="on"
                    class="ml-1 pb-1"
                    small
                    :color="getDebColor"
                  >
                    {{ getDebIcon }}
                  </v-icon>
                </template>
                <span>{{ getDebTooltip }}</span>
              </v-tooltip>
              <v-text-field
                v-model="form.deb_id"
                outlined
                dense
                hide-details="auto"
                readonly
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
              <label class="form-label">ABC ID</label>
              <v-text-field
                v-model="form.abc_id"
                outlined
                dense
                hide-details="auto"
                maxlength="12"
                :rules="[rules.number, rules.abcNumber]"
                :readonly="!form.deb_invalid"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
              <label class="form-label">Programme</label>
              <v-autocomplete
                v-model="form.program_id"
                outlined
                dense
                hide-details="auto"
                :items="programs"
                :item-text="
                  (item) =>
                    `${item.degree.short_name} ${
                      item.specialization ? `- ${item.specialization}` : ''
                    }`
                "
                item-value="id"
                readonly
                append-icon=""
              ></v-autocomplete>
            </v-col>
            <v-col cols="12" sm="6">
              <label class="form-label"
                >Name (As per the latest Certificate)</label
              >
              <v-text-field
                v-model="form.name"
                outlined
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <label class="form-label">Name (As per DEB)</label>
              <v-text-field
                v-model="form.deb_map.name"
                outlined
                dense
                readonly
              ></v-text-field>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="4">
          <label class="form-label"
            >Date of Birth <span class="error--text">* </span></label
          >
          <v-text-field
            v-model="formattedDob"
            dense
            outlined
            readonly
          />
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label"
            >Mobile Number <span class="error--text">* </span>
          </label>
          <template>
            <div class="d-flex align-center">
              <v-text-field
                v-model="form.mobile_prefix"
                outlined
                dense
                maxlength="5"
                class="mr-2"
                readonly
                style="max-width: 60px"
              ></v-text-field>

              <v-text-field
                v-model="form.mobile_no"
                outlined
                dense
                readonly
              ></v-text-field>
            </div>
          </template>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label">WhatsApp Number</label>
          <template>
            <div class="d-flex align-center">
              <v-autocomplete
                v-model="form.whatsapp_mobile_prefix"
                outlined
                dense
                maxlength="5"
                :items="countryCodes"
                class="mr-2"
                style="max-width: 100px"
                hide-details
                :readonly="isReadOnly"
                :append-icon="isReadOnly ? '' : 'mdi-menu-down'"
              ></v-autocomplete>
              <v-text-field
                v-model="form.whatsapp_mobile_no"
                outlined
                dense
                hide-details
                :readonly="isReadOnly"
                :rules="[rules.number, rules.mobile]"
                :maxlength="form.mobile_prefix === '+91' ? 10 : 15"
              ></v-text-field>
            </div>
          </template>
        </v-col>

        <v-col cols="12" md="4">
          <label class="form-label"
            >Personal Email Id <span class="error--text">* </span></label
          >
          <v-text-field
            v-model="form.email"
            outlined
            dense
            readonly
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label">Alternate Email Id</label>
          <v-text-field
            v-model="form.alternate_email"
            outlined
            dense
            hide-details="auto"
            :readonly="isReadOnly"
            :rules="[rules.email]"
          ></v-text-field>
        </v-col>

        <v-col cols="12" md="4">
          <label class="form-label"
            >Nationality <span class="error--text">* </span></label
          >
          <v-autocomplete
            v-model="form.country_id"
            :items="countries"
            item-text="name"
            item-value="id"
            outlined
            dense
            hide-details="auto"
            :readonly="isReadOnly"
            :rules="[rules.required]"
            :append-icon="isReadOnly ? '' : 'mdi-menu-down'"
            attach
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label"
            >Religion <span class="error--text">* </span></label
          >
          <v-autocomplete
            v-model="form.religion_id"
            :items="religions"
            item-text="name"
            item-value="id"
            outlined
            dense
            hide-details="auto"
            :readonly="isReadOnly"
            :rules="[rules.required]"
            :append-icon="isReadOnly ? '' : 'mdi-menu-down'"
            attach
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label"
            >Reservation Category <span class="error--text">* </span></label
          >
          <v-autocomplete
            v-model="form.community_id"
            :items="communities"
            item-text="name"
            item-value="id"
            outlined
            dense
            hide-details="auto"
            :rules="[rules.required]"
            :readonly="isReadOnly"
            :append-icon="isReadOnly ? '' : 'mdi-menu-down'"
            attach
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label">Caste/Subset</label>
          <v-autocomplete
            v-model="form.caste_id"
            :items="castes"
            item-text="name"
            item-value="id"
            outlined
            dense
            hide-details="auto"
            :append-icon="isReadOnly ? '' : 'mdi-menu-down'"
            attach
          >
          </v-autocomplete>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label"
            >Gender <span class="error--text">* </span></label
          >
          <v-radio-group
            v-model="form.gender"
            row
            dense
            readonly
            :rules="[rules.required]"
            hide-details="auto"
            class="pt-0"
          >
            <v-radio label="Male" value="Male"></v-radio>
            <v-radio label="Female" value="Female"></v-radio>
            <v-radio label="Other" value="Other"></v-radio>
          </v-radio-group>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label">Are you serving in Air Force </label>
          <v-radio-group
            v-model="form.indian_air_force"
            :readonly="isReadOnly"
            row
            dense
            class="pt-0"
          >
            <v-radio label="Yes" value="true"></v-radio>
            <v-radio label="No" value="false"></v-radio>
          </v-radio-group>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label">Resident Status </label>
          <v-radio-group
            v-model="form.residence"
            row
            dense
            :readonly="isReadOnly"
            :rules="[rules.required]"
            hide-details="auto"
            class="pt-0 text-arrange"
          >
            <v-radio label="Resident Indian" value="true">
              <template v-slot:label>
                <span class="custom-label">Resident Indian</span>
              </template></v-radio
            >
            <v-radio
              class="text-arrange"
              label="Non Resident Indian"
              value="false"
              ><template v-slot:label>
                <span class="custom-label">Non Resident Indian</span>
              </template></v-radio
            >
          </v-radio-group>
        </v-col>
        <v-col cols="12" md="4" v-if="form.residence === 'false'">
          <label class="form-label"
            >Passport Number <span class="error--text">* </span></label
          >
          <v-text-field
            v-model="form.passport_number"
            outlined
            dense
            hide-details="auto"
            :readonly="isReadOnly"
            :rules="[rules.required, rules.passportNumberRule]"
            @change="form.passport_number = form.passport_number.toUpperCase()"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4" v-if="form.residence === 'false'">
          <label class="form-label"
            >Date of Issue <span class="error--text">* </span></label
          >
          <v-menu
            v-model="menu1"
            :close-on-content-click="false"
            transition="scale-transition"
            max-width="290"
            offset-y
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="formattedPassportDate"
                dense
                outlined
                readonly
                placeholder="dd-mm-yyyy"
                :rules="[rules.required]"
                v-bind="attrs"
                hide-details="auto"
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="form.passport_date_of_issue"
              @input="menu1 = false"
              no-title
              :max="today"
              :readonly="isReadOnly"
            />
          </v-menu>
        </v-col>
        <v-col cols="12" md="4" v-if="form.residence === 'false'">
          <label class="form-label"
            >Place of issue <span class="error--text">* </span></label
          >
          <v-text-field
            v-model="form.passport_place_of_issue"
            outlined
            dense
            :readonly="isReadOnly"
            :rules="[rules.required]"
            hide-details="auto"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4" v-if="form.residence != 'false'">
          <label class="form-label"
            >Aadhaar Number <span class="error--text">* </span></label
          >
          <v-text-field
            v-model="form.aadhar_card_no"
            outlined
            dense
            maxlength="12"
            hide-details="auto"
            :readonly="isReadOnly"
            :rules="[rules.required, rules.aadhaarNumber]"
          ></v-text-field>
        </v-col>
        <v-col
          cols="12"
          md="3"
          v-if="form.residence != 'false'"
          class="pr-0 mr-0"
        >
          <label class="form-label inputFile">
            Aadhar Card Upload <span class="error--text">*</span>
          </label>

          <v-text-field
            v-model="form.aadhar.file_name"
            dense
            readonly
            outlined
            @click="
              form.aadhar.file_name
                ? openDialog({
                    type: form.aadhar.document_type,
                    file_binary: form.aadhar.file_binary,
                  })
                : ''
            "
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="1" class="d-flex justify-center pl-0 ml-0">
          <v-icon
            class="cursor-pointer"
            color="green"
            @click="$refs.aadharInput.click()"
            :disabled="isReadOnly"
          >
            mdi-upload
          </v-icon>

          <!-- Hidden File Input for Replace -->
          <input
            type="file"
            ref="aadharInput"
            style="display: none"
            accept="application/pdf, image/png, image/jpeg, image/jpg"
            @change="onFileReplaced($event, 'aadhar', 'aadharImage')"
          />
        </v-col>
        <v-col cols="12" md="4"></v-col>
        <v-col cols="12" md="4" v-if="form.residence != 'false'">
          <label class="form-label">PAN Number</label>
          <v-text-field
            v-model="form.pan_card_no"
            outlined
            dense
            hide-details="auto"
            maxlength="10"
            :readonly="isReadOnly"
            :rules="[rules.panNumber]"
            @input="form.pan_card_no = form.pan_card_no.toUpperCase()"
          ></v-text-field>
        </v-col>
        <v-col
          cols="12"
          md="3"
          v-if="form.residence != 'false'"
          class="pr-0 mr-0"
        >
          <label class="form-label">PAN card upload </label>
          <v-text-field
            v-model="form.pan.file_name"
            dense
            readonly
            outlined
            @click="
              form.pan.file_name
                ? openDialog({
                    type: form.pan.document_type,
                    file_binary: form.pan.file_binary,
                  })
                : ''
            "
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="1" class="d-flex justify-center pl-0 ml-0">
          <v-icon
            class="cursor-pointer"
            color="green"
            @click="$refs.panInput.click()"
            :disabled="isReadOnly"
          >
            mdi-upload
          </v-icon>

          <!-- Hidden File Input for Replace -->
          <input
            type="file"
            ref="panInput"
            style="display: none"
            accept="application/pdf, image/png, image/jpeg, image/jpg"
            @change="onFileReplaced($event, 'pan', 'panImage')"
          />
        </v-col>
      </v-row>
      <v-card elevation="0" class="d-flex justify-between">
        <v-card-title class="flex pl-0">Parent/Spouse/Guardian</v-card-title>
      </v-card>
      <v-row>
        <v-col cols="12" md="4">
          <label class="form-label"
            >Relation <span class="error--text">* </span></label
          >
          <v-autocomplete
            v-model="form.family.relationship"
            :items="relations"
            item-text="name"
            item-value="id"
            outlined
            dense
            :readonly="isReadOnly"
            :rules="[rules.required]"
            required
            hide-details="auto"
            :append-icon="isReadOnly ? '' : 'mdi-menu-down'"
            attach
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="4" v-if="form.family.relationship === 'Other'">
          <label class="form-label"
            >Specify the relation <span class="error--text">* </span></label
          >
          <v-text-field
            v-model="form.family.relation_type"
            outlined
            dense
            hide-details="auto"
            :readonly="isReadOnly"
            :rules="[rules.required]"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label"
            >Name <span class="error--text">* </span></label
          >
          <v-text-field
            v-model="form.family.name"
            outlined
            dense
            hide-details="auto"
            :readonly="isReadOnly"
            :rules="[rules.required, rules.onlyString]"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label"
            >Mobile Number <span class="error--text">* </span></label
          >
          <template>
            <div class="d-flex align-center justify-center">
              <v-autocomplete
                v-model="form.family.mobile_prefix"
                outlined
                dense
                maxlength="5"
                :items="countryCodes"
                class="mr-2"
                style="max-width: 100px"
                hide-details
                :readonly="isReadOnly"
                :rules="[rules.required]"
                :append-icon="isReadOnly ? '' : 'mdi-menu-down'"
                attach
                :menu-props="{ top: true, offsetY: true }"
              ></v-autocomplete>

              <v-text-field
                v-model="form.family.mobile_no"
                outlined
                dense
                :rules="[rules.required, rules.number, rules.mobile]"
                :maxlength="form.mobile_prefix === '+91' ? 10 : 15"
                :readonly="isReadOnly"
                hide-details
              ></v-text-field>
            </div>
          </template>
        </v-col>
        <v-col cols="12" md="4">
          <label class="form-label">Email Id </label>
          <v-text-field
            v-model="form.family.email"
            outlined
            dense
            :readonly="isReadOnly"
            :rules="[rules.email]"
            hide-details="auto"
          ></v-text-field>
        </v-col>
      </v-row>
      <!-- Buttons -->
      <v-row v-if="!isReadOnly && !isAdmitted" class="form-row mt-6">
        <v-col cols="12" class="d-flex justify-end pr-0">
          <v-btn
            color="success"
            class="mr-3"
            :disabled="JSON.stringify(this.value) === JSON.stringify(this.form)"
            @click="submit"
          >
            Save & Next
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    <attachmentViewer
      v-if="dialog"
      @closeDialog="dialog = false"
      :value="dialogData"
    />
  </v-container>
</template>

<script>
import { rules } from "@/helpers/rules";
import ProfileUpload from "./ProfileUpload.vue";
import attachmentViewer from "@/components/attachmentViewer.vue";
import { getCountries, getCountryCallingCode } from "libphonenumber-js";

export default {
  components: { ProfileUpload, attachmentViewer },
  props: {
    value: {
      type: Object,
      required: true,
    },
    isReadOnly: {
      type: Boolean,
      default: () => true,
    },
  },
  data: () => ({
    rules,
    menu: false,
    menu1: false,
    valid: false,
    today: new Date().toISOString().substr(0, 10),
    relations: ["Father", "Mother", "Spouse", "Guardian", "Other"],
    castes: [],
    communities: [],
    programs: [],
    religions: [],
    countries: [],
    form: {},
    dialog: false,
    dialogData: {},
    countryCodes: [],
  }),
  watch: {
    value: {
      handler(newVal) {
        this.form = JSON.parse(JSON.stringify(newVal));
        this.$emit("validate", true);
      },
      deep: true,
    },
    valid(newValue) {
      if (
        !newValue ||
        JSON.stringify(this.value) !== JSON.stringify(this.form)
      ) {
        this.$emit("validate", false);
      } else {
        this.$emit("validate", true);
      }
    },
    form: {
      handler(newVal) {
        if (
          !this.valid ||
          JSON.stringify(this.value) !== JSON.stringify(newVal)
        ) {
          this.$emit("validate", false);
        } else {
          this.$emit("validate", true);
        }
      },
      deep: true,
    },
  },
  computed: {
    isAdmitted() {
      return (
        (this.form.application_status || this.value.application_status || "")
          .toString()
          .toLowerCase() === "admitted"
      );
    },
    formattedDob() {
      if (!this.form.dob) return "";
      const [year, month, day] = this.form.dob.split("-");
      return `${day}-${month}-${year}`;
    },
    formattedPassportDate() {
      if (!this.form.passport_date_of_issue) return "";
      const [year, month, day] = this.form.passport_date_of_issue.split("-");
      return `${day}-${month}-${year}`;
    },
    getDebIcon() {
      if (!this.form.deb_validated) {
        return "mdi-alert-circle-outline";
      } else if (this.form.deb_invalid) {
        return "mdi-close-circle";
      } else {
        return "mdi-check-circle";
      }
    },
    getDebColor() {
      if (!this.form.deb_validated) {
        return "orange";
      } else if (this.form.deb_invalid) {
        return "red";
      } else {
        return "green";
      }
    },
    getDebTooltip() {
      if (!this.form.deb_validated) {
        return "DEB ID could not be validated";
      } else if (this.form.deb_invalid) {
        return "Invalid DEB ID";
      } else {
        return "DEB ID validated";
      }
    },
  },
  created() {
    this.form = JSON.parse(JSON.stringify(this.value));
    this.init();
  },
  methods: {
    onFileChanged(file, value, boolean) {
      this.form[value] = file.target.files[0];
      this.form[boolean] = false;
    },
    onFileReplaced(event, value, newValue) {
      const file = event.target.files[0];
      if (!file) return;

      // Replace the existing upload_image object
      const reader = new FileReader();
      reader.onload = (e) => {
        const [type, _] = file.type.split("/");
        const result = e.target.result;
        const base64 = result.split(",")[1];
        this.form[value] = {
          file_name: file.name,
          file_binary: base64,
          document_type: type,
        };
      };
      reader.readAsDataURL(file);
      this.form[newValue] = file;
    },
    async init() {
      await Promise.all([
        this.getReligions(),
        this.getCastes(),
        this.getCommunities(),
        this.getCountries(),
        this.getPrograms(),
        this.getCountryCodes(),
      ]);
    },
    getCountryCodes() {
      const codes = getCountries().map(
        (countryCode) => "+" + getCountryCallingCode(countryCode)
      );
      this.countryCodes = [...new Set(codes)].sort((a, b) => {
        return parseInt(a.slice(1)) - parseInt(b.slice(1));
      });
    },
    async getPrograms() {
      try {
        const response = await this.$axios.get("/programs");
        this.programs = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getReligions() {
      try {
        const response = await this.$axios.get("/religions");
        this.religions = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getCastes() {
      try {
        const response = await this.$axios.get("/castes");
        this.castes = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getCommunities() {
      try {
        const response = await this.$axios.get("/communities");
        this.communities = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getCountries() {
      try {
        const response = await this.$axios.get("/countries");
        this.countries = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    openDialog(data) {
      this.dialogData = data;
      this.dialog = true;
    },

    async submit() {
      if (!this.$refs.form.validate()) {
        window.scrollTo(0, 0);
        return;
      }
      this.$emit("validate", true);
      this.$emit("submit", this.form);
    },
  },
};
</script>

<style scoped>
.input-col {
  padding-top: 0; /* Ensure inputs align properly */
}

.form-label {
  font-weight: 500;
  color: rgba(0, 0, 0, 0.8);
}

.custom-label {
  font-size: 14px;
  line-height: 1rem;
}
::v-deep(.v-messages) {
  /* color: red !important; */
  color: var(--v-messages-color, red) !important;
}
</style>
